import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { EventOrigin, Events, track } from "apis/procore";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import type { Node } from "components/objectTree/types";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { highlightActions, useDispatchHighlighted } from "contexts/highlighted";
import type { ModelTreeNodeData } from "features/modelTree/types";
import { selectDefaultVisibility, selectPreIsolationVisibility } from "features/render";
import { useAbortController } from "hooks/useAbortController";
import IsolateActionIcon from "media/icons/isolate-action.svg?react";
import { isolateObjects, selectObjectHierarchy } from "utils/objectActions";

import { useRowSwipe } from "../hooks/useRowSwipe";

export type SwipeableRowProps = {
    node: Node<ModelTreeNodeData>;
    onOpen: (close: () => void) => void;
    onClose?: () => void;
    children: React.ReactNode;
};

export function SwipeableRow({ node, onOpen, onClose, children }: SwipeableRowProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const {
        state: { db },
    } = useExplorerGlobals(true);
    const currentDefaultVisibility = useAppSelector(selectDefaultVisibility);
    const preIsolationVisibility = useAppSelector(selectPreIsolationVisibility);
    const dispatchHighlighted = useDispatchHighlighted();
    const [abortController] = useAbortController();
    const [isBusy, setIsBusy] = useState(false);
    const { contentRef, isRevealed, close } = useRowSwipe();

    const prevRevealedRef = useRef(false);
    useEffect(() => {
        if (isRevealed && !prevRevealedRef.current) {
            onOpen(close);
        } else if (!isRevealed && prevRevealedRef.current) {
            onClose?.();
        }
        prevRevealedRef.current = isRevealed;
    }, [isRevealed, onOpen, onClose, close]);

    const handleIsolate = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isBusy) {
            return;
        }
        dispatchHighlighted(highlightActions.setIds([]));
        const affectedIds = await selectObjectHierarchy({
            nodes: [node.data.object],
            db,
            abortSignal: abortController.current.signal,
            setLoading: setIsBusy,
            dispatchHighlighted,
        });
        if (affectedIds.length > 0) {
            isolateObjects({
                dispatch,
                isolationIds: affectedIds,
                currentDefaultVisibility: preIsolationVisibility ?? currentDefaultVisibility,
            });
            void track(Events.bimObjects.isolated, { origin: EventOrigin.ObjectTree });
        }
    };

    const actionStyle: React.CSSProperties = {
        width: 80,
        alignSelf: "stretch",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        border: "none",
        background: "rgba(172, 181, 185, 0.32)",
        cursor: "pointer",
        padding: 0,
        opacity: isBusy ? 0.5 : 1,
        pointerEvents: isBusy ? "none" : "auto",
    };

    const iconLabelStyle: React.CSSProperties = {
        fontSize: 14,
        fontWeight: 400,
        color: "#000000",
        fontFamily: "inherit",
        lineHeight: "18px",
    };

    return (
        <div style={{ position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, display: "flex" }}>
                <button
                    type="button"
                    aria-label={t("isolate")}
                    onClick={(e) => void handleIsolate(e)}
                    aria-hidden={!isRevealed}
                    tabIndex={isRevealed ? undefined : -1}
                    style={actionStyle}
                >
                    <IsolateActionIcon style={{ width: 18, height: 18 }} aria-hidden="true" />
                    <span style={iconLabelStyle}>{t("isolate")}</span>
                </button>
            </div>
            {/* Sliding content — white background covers the button until swiped */}
            <div ref={contentRef} style={{ position: "relative", zIndex: 1, background: "white", width: "100%" }}>
                {children}
            </div>
        </div>
    );
}
